import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { apiPost, apiGet } from '../../utils/api';

interface Material {
    material: string;
    type: 'Nitrogen' | 'Carbon';
    source: string;
    weight_kg: number;
    cost_per_kg: number;
    moisture_pct: number;
}

const ProfessionalBatchModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
    // Section 1: Batch Identity
    const [batchNumber, setBatchNumber] = useState(`CB-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-001`);
    const [productionMethod, setProductionMethod] = useState('Windrow');
    const [facility, setFacility] = useState('');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [maturityDate, setMaturityDate] = useState('');
    const [supervisor, setSupervisor] = useState('');
    const [batchStatus, setBatchStatus] = useState('In Progress');

    // Section 2: Raw Material Inputs
    const [materials, setMaterials] = useState<Material[]>([
        { material: 'Cow Manure', type: 'Nitrogen', source: '', weight_kg: 0, cost_per_kg: 0, moisture_pct: 0 }
    ]);

    // Section 3: Composting Infrastructure
    const [pileLength, setPileLength] = useState(0);
    const [pileWidth, setPileWidth] = useState(0);
    const [pileHeight, setPileHeight] = useState(0);
    const [turningMethod, setTurningMethod] = useState('Manual');

    // Section 4: Environmental Conditions
    const [initialMoisture, setInitialMoisture] = useState(55);
    const [initialTemp, setInitialTemp] = useState(48);
    const [ambientTemp, setAmbientTemp] = useState(25);
    const [waterAdded, setWaterAdded] = useState(0);
    const [phLevel, setPhLevel] = useState(6.8);

    // Section 5: Monitoring Plan
    const [turningFrequency, setTurningFrequency] = useState(7);
    const [nextTurning, setNextTurning] = useState('');
    const [monitoringFrequency, setMonitoringFrequency] = useState('Daily');
    const [responsibleWorker, setResponsibleWorker] = useState('');

    // Section 6: Output Targets
    const [targetQuality, setTargetQuality] = useState('Premium');
    const [targetMoisture, setTargetMoisture] = useState(40);
    const [targetTexture, setTargetTexture] = useState('Fine crumbly');
    const [compostStarter, setCompostStarter] = useState(false);
    const [starterType, setStarterType] = useState('');

    const [submitting, setSubmitting] = useState(false);

    // Section 1: Field/Farmer Linkage
    const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
    const [selectedFarmerId, setSelectedFarmerId] = useState<number | null>(null);
    const [fields, setFields] = useState<any[]>([]);

    // Fetch fields for linkage — farmers are auto-derived from the selected field
    useEffect(() => {
        const fetchData = async () => {
            try {
                const fieldsRes: any = await apiGet('/api/maps/farm-plots').catch(() => []);
                setFields(Array.isArray(fieldsRes) ? fieldsRes : (fieldsRes?.plots || []));
            } catch {
                // Silently handle — dropdown will just be empty
            }
        };
        fetchData();
    }, []);

    // Auto-derive farmer list from selected field
    const fieldFarmers = useMemo(() => {
        if (!selectedFieldId) return [];
        const field = fields.find((f: any) => f.id === selectedFieldId);
        return field?.farmers || (field?.farmer_id ? [{ id: field.farmer_id, name: field.farmer_name || field.primary_farmer_name }] : []);
    }, [selectedFieldId, fields]);

    // Auto-select the primary farmer when field changes
    useEffect(() => {
        if (fieldFarmers.length > 0) {
            const primary = fieldFarmers.find((f: any) => f.is_primary) || fieldFarmers[0];
            setSelectedFarmerId(primary?.id || null);
        } else {
            setSelectedFarmerId(null);
        }
    }, [fieldFarmers]);

    const totalInput = useMemo(() => materials.reduce((sum, m) => sum + m.weight_kg, 0), [materials]);
    const totalCarbon = useMemo(() => materials.filter(m => m.type === 'Carbon').reduce((sum, m) => sum + m.weight_kg, 0), [materials]);
    const totalNitrogen = useMemo(() => materials.filter(m => m.type === 'Nitrogen').reduce((sum, m) => sum + m.weight_kg, 0), [materials]);
    const cnRatio = totalNitrogen > 0 ? (totalCarbon / totalNitrogen).toFixed(1) : '0';
    const expectedOutput = totalInput * 0.5;
    const pileVolume = pileLength * pileWidth * pileHeight;
    const estimatedCapacity = useMemo(() => pileVolume * 600, [pileVolume]);

    const cnStatus = useMemo(() => {
        const ratio = parseFloat(cnRatio);
        if (ratio >= 25 && ratio <= 35) return 'optimal';
        if (ratio >= 20 && ratio <= 40) return 'slight';
        return 'needs';
    }, [cnRatio]);

    const addMaterial = useCallback(() => {
        setMaterials(prev => [...prev, { material: '', type: 'Carbon', source: '', weight_kg: 0, cost_per_kg: 0, moisture_pct: 0 }]);
    }, []);

    const removeMaterial = useCallback((idx: number) => {
        setMaterials(prev => prev.filter((_, i) => i !== idx));
    }, []);

    const updateMaterialField = useCallback((idx: number, field: keyof Material, value: any) => {
        setMaterials(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
    }, []);

    const handleSubmit = async () => {
        if (!startDate || totalInput === 0) {
            alert('Start date and materials are required');
            return;
        }
        setSubmitting(true);
        try {
            await apiPost('/api/compost/batches', {
                batch_number: batchNumber,
                cohort_id: null,
                production_date: startDate,
                maturity_date: maturityDate || null,
                quantity_kg: expectedOutput,
                location: facility,
                compost_method: productionMethod,
                supervisor: supervisor,
                status: batchStatus.toLowerCase().replace(' ', '_'),
                manure_used_kg: totalNitrogen,
                carbon_materials_kg: totalCarbon,
                water_added_liters: waterAdded,
                expected_output_kg: expectedOutput,
                pile_length_m: pileLength,
                pile_width_m: pileWidth,
                pile_height_m: pileHeight,
                turning_method: turningMethod,
                initial_moisture_pct: initialMoisture,
                initial_temp_c: initialTemp,
                ph_level: phLevel,
                turning_frequency_days: turningFrequency,
                target_quality: targetQuality,
                field_plot_id: selectedFieldId,
                farmer_id: selectedFarmerId,
                notes: `Materials: ${materials.map(m => `${m.material} (${m.weight_kg}kg)`).join(', ')}`
            });
            onCreated();
            onClose();
        } catch {
            alert('Failed to create batch');
        } finally {
            setSubmitting(false);
        }
    };

    // Simple input handler that doesn't cause re-render issues
    const handleInputChange = useCallback((setter: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setter(e.target.value);
    }, []);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            {/* Backdrop */}
            <div className="absolute inset-0" onClick={onClose} />
            
            {/* Modal Content */}
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col relative z-10">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-700 to-emerald-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="text-white">
                        <h2 className="text-xl font-bold">🌱 Professional Compost Production Engineering</h2>
                        <p className="text-green-100 text-sm">Complete batch engineering form</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-white/80 hover:text-white">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Scrollable Form Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    
                    {/* Section 1: Batch Identity */}
                    <div className="bg-gray-50 rounded-xl p-5">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">1. Batch Identity</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Batch Number</label>
                                <input 
                                    type="text"
                                    value={batchNumber} 
                                    onChange={handleInputChange(setBatchNumber)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Production Method</label>
                                <select 
                                    value={productionMethod} 
                                    onChange={handleInputChange(setProductionMethod)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                    <option>Windrow</option>
                                    <option>Compost Pit</option>
                                    <option>Compost Bin</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Facility Location</label>
                                <input 
                                    type="text"
                                    value={facility} 
                                    onChange={handleInputChange(setFacility)}
                                    placeholder="Farm Site A – Windrow Zone 3"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date *</label>
                                <input 
                                    type="date" 
                                    value={startDate} 
                                    onChange={handleInputChange(setStartDate)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Expected Maturity</label>
                                <input 
                                    type="date" 
                                    value={maturityDate} 
                                    onChange={handleInputChange(setMaturityDate)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Batch Supervisor</label>
                                <input 
                                    type="text"
                                    value={supervisor} 
                                    onChange={handleInputChange(setSupervisor)}
                                    placeholder="Jean Pierre"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Batch Status</label>
                                <select 
                                    value={batchStatus} 
                                    onChange={handleInputChange(setBatchStatus)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                    <option>In Progress</option>
                                    <option>Curing</option>
                                    <option>Mature</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Linked Field / Plot</label>
                                <select 
                                    value={selectedFieldId ?? ''} 
                                    onChange={(e) => setSelectedFieldId(e.target.value ? Number(e.target.value) : null)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                    <option value="">— None —</option>
                                    {fields.map((f: any) => (
                                        <option key={f.id} value={f.id}>
                                            {f.name || f.plot_code || `Field #${f.id}`}{f.crop_type ? ` (${f.crop_type})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Linked Farmer
                                    {selectedFieldId && fieldFarmers.length > 0 && (
                                        <span className="ml-1 text-xs font-normal text-gray-400">(auto from field)</span>
                                    )}
                                </label>
                                <select 
                                    value={selectedFarmerId ?? ''} 
                                    onChange={(e) => setSelectedFarmerId(e.target.value ? Number(e.target.value) : null)}
                                    disabled={!selectedFieldId}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400">
                                    <option value="">
                                        {!selectedFieldId ? 'Select a field first' : fieldFarmers.length === 0 ? '— No farmers on this field —' : '— None —'}
                                    </option>
                                    {fieldFarmers.map((f: any) => (
                                        <option key={f.id} value={f.id}>
                                            {f.name || f.full_name || `Farmer #${f.id}`}{f.role_on_field ? ` (${f.role_on_field})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Raw Material Inputs */}
                    <div className="bg-gray-50 rounded-xl p-5">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">2. Raw Material Inputs</h3>
                        <div className="space-y-3">
                            {materials.map((mat, idx) => (
                                <div key={idx} className="flex flex-wrap gap-2 p-3 bg-white rounded-lg items-end">
                                    <div className="flex-1 min-w-[120px]">
                                        <label className="block text-xs text-gray-500 mb-1">Material</label>
                                        <input 
                                            type="text"
                                            value={mat.material} 
                                            onChange={(e) => updateMaterialField(idx, 'material', e.target.value)}
                                            placeholder="Material"
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" 
                                        />
                                    </div>
                                    <div className="w-24">
                                        <label className="block text-xs text-gray-500 mb-1">Type</label>
                                        <select 
                                            value={mat.type} 
                                            onChange={(e) => updateMaterialField(idx, 'type', e.target.value)}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                                            <option>Nitrogen</option>
                                            <option>Carbon</option>
                                        </select>
                                    </div>
                                    <div className="flex-1 min-w-[100px]">
                                        <label className="block text-xs text-gray-500 mb-1">Source</label>
                                        <input 
                                            type="text"
                                            value={mat.source} 
                                            onChange={(e) => updateMaterialField(idx, 'source', e.target.value)}
                                            placeholder="Source"
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" 
                                        />
                                    </div>
                                    <div className="w-24">
                                        <label className="block text-xs text-gray-500 mb-1">Weight (kg)</label>
                                        <input 
                                            type="number"
                                            value={mat.weight_kg} 
                                            onChange={(e) => updateMaterialField(idx, 'weight_kg', +e.target.value)}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" 
                                        />
                                    </div>
                                    <div className="w-20">
                                        <label className="block text-xs text-gray-500 mb-1">Cost/kg</label>
                                        <input 
                                            type="number"
                                            value={mat.cost_per_kg} 
                                            onChange={(e) => updateMaterialField(idx, 'cost_per_kg', +e.target.value)}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" 
                                        />
                                    </div>
                                    <div className="w-20">
                                        <label className="block text-xs text-gray-500 mb-1">Moisture %</label>
                                        <input 
                                            type="number"
                                            value={mat.moisture_pct} 
                                            onChange={(e) => updateMaterialField(idx, 'moisture_pct', +e.target.value)}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" 
                                        />
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={() => removeMaterial(idx)} 
                                        className="text-red-600 hover:text-red-800 p-1.5"
                                        disabled={materials.length === 1}
                                    >
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            ))}
                            <button type="button" onClick={addMaterial} className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-semibold hover:bg-green-200 flex items-center gap-2">
                                <PlusIcon className="w-4 h-4" /> Add Material
                            </button>
                            
                            {/* Summary */}
                            <div className="grid grid-cols-3 gap-3 mt-4">
                                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                    <p className="text-xs text-amber-700 font-semibold">Total Carbon</p>
                                    <p className="text-xl font-bold text-amber-900">{totalCarbon.toLocaleString()} kg</p>
                                </div>
                                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <p className="text-xs text-blue-700 font-semibold">Total Nitrogen</p>
                                    <p className="text-xl font-bold text-blue-900">{totalNitrogen.toLocaleString()} kg</p>
                                </div>
                                <div className={`p-3 rounded-lg border-2 ${cnStatus === 'optimal' ? 'bg-green-50 border-green-500' : cnStatus === 'slight' ? 'bg-yellow-50 border-yellow-500' : 'bg-red-50 border-red-500'}`}>
                                    <p className="text-xs font-semibold">C:N Ratio</p>
                                    <p className="text-xl font-bold">{cnRatio}:1</p>
                                    <p className="text-xs mt-1">
                                        {cnStatus === 'optimal' ? '🟢 Optimal' : cnStatus === 'slight' ? '🟡 Slight imbalance' : '🔴 Needs adjustment'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Composting Infrastructure */}
                    <div className="bg-gray-50 rounded-xl p-5">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">3. Composting Method & Infrastructure</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Pile Length (m)</label>
                                <input 
                                    type="number"
                                    value={pileLength} 
                                    onChange={(e) => setPileLength(+e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Pile Width (m)</label>
                                <input 
                                    type="number"
                                    value={pileWidth} 
                                    onChange={(e) => setPileWidth(+e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Pile Height (m)</label>
                                <input 
                                    type="number"
                                    value={pileHeight} 
                                    onChange={(e) => setPileHeight(+e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Turning Method</label>
                                <select 
                                    value={turningMethod} 
                                    onChange={handleInputChange(setTurningMethod)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                    <option>Manual</option>
                                    <option>Tractor</option>
                                    <option>Compost Turner</option>
                                </select>
                            </div>
                            <div className="md:col-span-2 grid grid-cols-2 gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div>
                                    <p className="text-sm text-blue-700 font-semibold">Volume</p>
                                    <p className="text-2xl font-bold text-blue-900">{pileVolume.toFixed(2)} m³</p>
                                </div>
                                <div>
                                    <p className="text-sm text-blue-700 font-semibold">Estimated Capacity</p>
                                    <p className="text-2xl font-bold text-blue-900">{estimatedCapacity.toLocaleString()} kg</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 4: Environmental Conditions */}
                    <div className="bg-gray-50 rounded-xl p-5">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">4. Environmental Conditions</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Initial Moisture (%)</label>
                                <input 
                                    type="number"
                                    value={initialMoisture} 
                                    onChange={(e) => setInitialMoisture(+e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                                <p className="text-xs text-gray-500 mt-1">Ideal: 50-60%</p>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Initial Temperature (°C)</label>
                                <input 
                                    type="number"
                                    value={initialTemp} 
                                    onChange={(e) => setInitialTemp(+e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                                <p className="text-xs text-gray-500 mt-1">Ideal: 50-65°C</p>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Ambient Temperature (°C)</label>
                                <input 
                                    type="number"
                                    value={ambientTemp} 
                                    onChange={(e) => setAmbientTemp(+e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Water Added (liters)</label>
                                <input 
                                    type="number"
                                    value={waterAdded} 
                                    onChange={(e) => setWaterAdded(+e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">pH Level</label>
                                <input 
                                    type="number"
                                    step="0.1"
                                    value={phLevel} 
                                    onChange={(e) => setPhLevel(+e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                                <p className="text-xs text-gray-500 mt-1">Ideal: 6.5-8.0</p>
                            </div>
                        </div>
                    </div>

                    {/* Section 5: Production Monitoring Plan */}
                    <div className="bg-gray-50 rounded-xl p-5">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">5. Production Monitoring Plan</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Turning Frequency (days)</label>
                                <input 
                                    type="number"
                                    value={turningFrequency} 
                                    onChange={(e) => setTurningFrequency(+e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Next Turning Date</label>
                                <input 
                                    type="date"
                                    value={nextTurning} 
                                    onChange={handleInputChange(setNextTurning)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Monitoring Frequency</label>
                                <select 
                                    value={monitoringFrequency} 
                                    onChange={handleInputChange(setMonitoringFrequency)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                    <option>Daily</option>
                                    <option>Every 2 days</option>
                                    <option>Weekly</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Responsible Worker</label>
                                <input 
                                    type="text"
                                    value={responsibleWorker} 
                                    onChange={handleInputChange(setResponsibleWorker)}
                                    placeholder="Eric"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Section 6: Output & Quality Targets */}
                    <div className="bg-gray-50 rounded-xl p-5">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">6. Output & Quality Targets</h3>
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-300">
                                <div>
                                    <p className="text-sm text-green-700 font-semibold">Total Raw Input</p>
                                    <p className="text-2xl font-bold text-green-900">{totalInput.toLocaleString()} kg</p>
                                </div>
                                <div>
                                    <p className="text-sm text-amber-700 font-semibold">Expected Mass Loss</p>
                                    <p className="text-2xl font-bold text-amber-900">50%</p>
                                </div>
                                <div>
                                    <p className="text-sm text-blue-700 font-semibold">Expected Compost Output</p>
                                    <p className="text-2xl font-bold text-blue-900">{expectedOutput.toLocaleString()} kg</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Target Quality Grade</label>
                                    <select 
                                        value={targetQuality} 
                                        onChange={handleInputChange(setTargetQuality)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                        <option>Premium</option>
                                        <option>Good</option>
                                        <option>Fair</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Target Moisture (%)</label>
                                    <input 
                                        type="number"
                                        value={targetMoisture} 
                                        onChange={(e) => setTargetMoisture(+e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Target Texture</label>
                                    <input 
                                        type="text"
                                        value={targetTexture} 
                                        onChange={handleInputChange(setTargetTexture)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                    />
                                </div>
                            </div>
                            <div className="flex items-center gap-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                <input 
                                    type="checkbox" 
                                    checked={compostStarter} 
                                    onChange={(e) => setCompostStarter(e.target.checked)}
                                    className="w-4 h-4" 
                                />
                                <label className="text-sm font-semibold text-gray-700">Add Microbial Activation / Compost Starter?</label>
                                {compostStarter && (
                                    <input 
                                        type="text"
                                        value={starterType} 
                                        onChange={handleInputChange(setStarterType)}
                                        placeholder="EM solution / old compost / ash"
                                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" 
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center flex-shrink-0">
                    <div className="text-sm text-gray-600">
                        <span className="font-semibold">Total Input:</span> {totalInput.toLocaleString()} kg &nbsp;•&nbsp;
                        <span className="font-semibold">Expected Output:</span> {expectedOutput.toLocaleString()} kg &nbsp;•&nbsp;
                        <span className="font-semibold">C:N Ratio:</span> {cnRatio}:1
                    </div>
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
                        <button 
                            type="button" 
                            onClick={handleSubmit} 
                            disabled={submitting}
                            className="px-6 py-2 bg-gradient-to-r from-green-700 to-emerald-600 text-white rounded-xl text-sm font-semibold shadow hover:shadow-md transition-all disabled:opacity-60">
                            {submitting ? 'Creating...' : 'Create Professional Batch'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfessionalBatchModal;
